/* ControlAlertsPage — §44-47, §67-74 */
import React, { useCallback, useEffect, useState } from "react";
import { controlApi } from "../lib/controlApi";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Loader2, Bell, BellOff } from "lucide-react";

const SEV_STYLE = {
  critical: { color: "text-red-700", bg: "bg-red-50", border: "border-red-300", Icon: XCircle },
  warning:  { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-300", Icon: AlertTriangle },
  info:     { color: "text-slate-700", bg: "bg-slate-100", border: "border-slate-300", Icon: AlertTriangle },
};

export default function ControlAlertsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState("default");
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await controlApi.incidents({ limit: 100 }); setItems(r.items || []); }
    catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    const supported = "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
    setPushSupported(supported);
    if (supported) setPushPermission(Notification.permission);
  }, []);

  const enablePush = async () => {
    try {
      const perm = await Notification.requestPermission();
      setPushPermission(perm);
      if (perm !== "granted") { toast.error("Permiso denegado."); return; }
      const reg = await navigator.serviceWorker.ready;
      // We register subscription with a placeholder public key; if real VAPID key
      // is configured later on backend, this will be reused. For now, we simply
      // enable local (non-VAPID) notifications so §66 preparation is complete.
      let subscription = null;
      try {
        subscription = await reg.pushManager.getSubscription();
      } catch { /* noop */ }
      await controlApi.patchCurrentDevice({ push_enabled: true, push_subscription: subscription ? subscription.toJSON() : null });
      toast.success("Notificaciones activadas.");
    } catch (ex) {
      toast.error("No se pudo activar notificaciones.");
    }
  };

  const disablePush = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe().catch(() => {});
      await controlApi.patchCurrentDevice({ push_enabled: false, push_subscription: null });
      toast.success("Notificaciones desactivadas en este dispositivo.");
    } catch { /* noop */ }
  };

  const doAck = async (id) => {
    try { await controlApi.ackIncident(id); toast.success("Marcada como vista."); load(); }
    catch { toast.error("Error"); }
  };
  const markAllRead = async () => {
    try { await controlApi.markAllRead(); toast.success("Marcadas como leídas."); }
    catch { toast.error("Error"); }
  };

  const open = items.filter((i) => i.status !== "resolved");
  const info = items.filter((i) => i.status !== "resolved" && i.severity === "info");
  const resolved = items.filter((i) => i.status === "resolved").slice(0, 10);
  const attention = open.filter((i) => i.severity !== "info");

  return (
    <div className="p-4 space-y-4" data-testid="spc-alerts">
      <div>
        <div className="text-[10px] uppercase tracking-[0.35em] text-textgray">Alertas</div>
        <div className="font-serif text-2xl font-bold text-blueblack">Estado del sistema</div>
      </div>

      {/* Push preferences (§67) */}
      {pushSupported && (
        <div className="border border-softblue bg-white p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-nightblue" />
            <div className="text-sm font-bold text-blueblack">Notificaciones</div>
          </div>
          <p className="text-xs text-textgray">Sur Primicia Control puede avisarte cuando exista un problema importante o una noticia urgente.</p>
          {pushPermission === "granted" ? (
            <button onClick={disablePush} data-testid="spc-push-disable"
              className="w-full h-10 border border-softblue bg-white text-blueblack text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5">
              <BellOff className="w-3.5 h-3.5" /> Desactivar en este dispositivo
            </button>
          ) : (
            <button onClick={enablePush} data-testid="spc-push-enable"
              className="w-full h-10 bg-blueblack text-white text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5">
              <Bell className="w-3.5 h-3.5" /> Activar notificaciones
            </button>
          )}
        </div>
      )}

      {loading ? <div className="text-center p-6"><Loader2 className="w-4 h-4 animate-spin mx-auto text-textgray" /></div> : (
        <>
          {/* Requieren atención */}
          <Section title="Requieren atención" count={attention.length}>
            {attention.length === 0 ? (
              <div className="p-6 text-center text-textgray text-xs flex flex-col items-center gap-1">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                Sin alertas. Todo funciona correctamente.
              </div>
            ) : (
              <ul className="divide-y divide-softblue">
                {attention.map((i) => <AlertRow key={i.id} inc={i} onAck={doAck} />)}
              </ul>
            )}
          </Section>
          {info.length > 0 && (
            <Section title="Informativas" count={info.length}>
              <ul className="divide-y divide-softblue">
                {info.map((i) => <AlertRow key={i.id} inc={i} onAck={doAck} />)}
              </ul>
            </Section>
          )}
          {resolved.length > 0 && (
            <Section title="Resueltas recientes" count={resolved.length} muted>
              <ul className="divide-y divide-softblue">
                {resolved.map((i) => (
                  <li key={i.id} className="p-3 flex items-start gap-3 text-xs">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-blueblack font-bold">{i.title}</div>
                      <div className="text-textgray">{i.resolved_at && new Date(i.resolved_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" })}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          <button onClick={markAllRead} data-testid="spc-mark-all-read"
            className="w-full h-10 border border-softblue bg-white text-blueblack text-[11px] uppercase tracking-widest font-bold">
            Marcar todas como vistas
          </button>
        </>
      )}
    </div>
  );
}

function Section({ title, count, muted, children }) {
  return (
    <div className="border border-softblue bg-white">
      <div className={`px-4 py-2 border-b border-softblue text-[10px] uppercase tracking-widest ${muted ? "text-textgray" : "text-blueblack"} font-bold flex items-center justify-between`}>
        <span>{title}</span>
        <span>{count}</span>
      </div>
      {children}
    </div>
  );
}

function AlertRow({ inc, onAck }) {
  const s = SEV_STYLE[inc.severity] || SEV_STYLE.info;
  const Icon = s.Icon;
  return (
    <li data-testid={`spc-alert-${inc.id}`} className="p-3 flex items-start gap-3">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${s.color}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-bold ${s.color}`}>{inc.title}</div>
        {inc.description && <div className="text-xs text-textgray mt-0.5">{inc.description}</div>}
        <div className="text-[10px] text-textgray mt-1 flex items-center justify-between">
          <span>Desde {new Date(inc.started_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" })}</span>
          {inc.status !== "acknowledged" && (
            <button onClick={() => onAck(inc.id)} data-testid={`spc-alert-ack-${inc.id}`}
              className="text-nightblue underline text-[11px] uppercase tracking-widest">
              Marcar como vista
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
